import React, { useEffect, useState, useCallback } from "react";
import CityAutocomplete from "./components/CityAutocomplete";
import RouteMap, { DEFAULT_POWER_RANGES } from "./components/RouteMap";
import NearbyMunicipalitiesTables from "./components/NearbyMunicipalitiesTables";
import SettingsPanel from "./components/SettingsPanel";
import EVFilters from "./components/EVFilters";
import ChargersList from "./components/ChargersList";
import LanguageSwitcher from "./components/LanguageSwitcher";
import SuggestionBox from "./components/SuggestionBox";
import UserManual from "./components/UserManual";
import EVCarConfig, { getDefaultEVCarConfig, suggestChargingStops } from "./components/EVCarConfig";
import RoutesPopup from "./components/RoutesPopup";
import GroupPanel from "./components/GroupPanel";
import { useI18n } from "./i18n";

const API_URL = process.env.REACT_APP_API_URL || '';

const initialForm = {
  origin: { text: "", coordinates: null },
  destination: { text: "", coordinates: null },
};

async function geocode(query) {
  const response = await fetch(`${API_URL}/api/geocode?q=${encodeURIComponent(query)}&limit=1`);
  const data = await response.json();
  if (!response.ok || !data.success || !data.data || data.data.length === 0) {
    throw new Error(`No se encontró la ubicación: ${query}`);
  }
  return data.data[0].coordinates;
}

export default function App() {
  const { t, changeLocale } = useI18n();
  const [form, setForm] = useState(initialForm);
  const [health, setHealth] = useState({ status: "loading" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [distancia, setDistancia] = useState(null);
  
  // Estado para poblaciones cercanas
  const [kmPoints, setKmPoints] = useState([]);
  const [nearbyData, setNearbyData] = useState(null);
  const [nearbyLoading, setNearbyLoading] = useState(false);
  const [maxDeviation, setMaxDeviation] = useState(5);
  
  // Estado para municipios seleccionados (resaltados en el mapa)
  const [selectedMunicipalities, setSelectedMunicipalities] = useState([]);

  // Feature 1: Dark mode
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('evplanner-dark') === 'true');
  useEffect(() => {
    document.body.classList.toggle('dark', darkMode);
    localStorage.setItem('evplanner-dark', darkMode);
  }, [darkMode]);

  // Feature 4: Vehicle range for auto-suggest
  const [vehicleRange, setVehicleRange] = useState(300);

  // Feature 6: Export copied state
  const [exportCopied, setExportCopied] = useState(false);

  // Legal documents modal
  const [legalModal, setLegalModal] = useState(null);
  const [legalTexts, setLegalTexts] = useState({});
  useEffect(() => {
    if (legalModal && !legalTexts[legalModal]) {
      const files = {
        aviso: '/aviso_legal.txt',
        privacidad: '/politica_privacidad.txt',
        cookies: '/politica_de_cookies.txt',
        condiciones: '/condiciones_de_uso.txt'
      };
      fetch(files[legalModal])
        .then(r => r.text())
        .then(text => setLegalTexts(prev => ({ ...prev, [legalModal]: text })))
        .catch(() => setLegalTexts(prev => ({ ...prev, [legalModal]: 'Error al cargar el documento.' })));
    }
  }, [legalModal, legalTexts]);

  // Feature 6: Total chargers count
  const [totalChargers, setTotalChargers] = useState(null);
  useEffect(() => {
    fetch(`${API_URL}/api/chargers/all?noFilter=true`)
      .then(r => r.json())
      .then(d => { if (d.success) setTotalChargers(d.count || d.data?.length || 0); })
      .catch(() => {});
  }, []);

  // Tabs: 'map' (default), 'stops', 'search'
  const [activeTab, setActiveTab] = useState('map');

  // Feature: Route history
  const [routeHistory, setRouteHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem('evplanner-history') || '[]'); } catch { return []; }
  });

  // Feature: Favorite chargers
  const [favoriteChargers, setFavoriteChargers] = useState(() => {
    try { return JSON.parse(localStorage.getItem('evplanner-favorites') || '[]'); } catch { return []; }
  });
  useEffect(() => { localStorage.setItem('evplanner-favorites', JSON.stringify(favoriteChargers)); }, [favoriteChargers]);

  const toggleFavorite = useCallback((charger) => {
    setFavoriteChargers(prev => {
      const exists = prev.some(f => f.id === charger.id);
      return exists ? prev.filter(f => f.id !== charger.id) : [...prev.slice(-49), charger];
    });
  }, []);

  // Feature: Charger data for map markers
  const [routeChargers, setRouteChargers] = useState([]);
  // Chargers currently drawn on the map (filtered). Reported by RouteMap
  // so that the RoutesPopup can calculate routes using ONLY chargers the
  // user has visualised via stop points / exploration pins.
  const [mapVisibleChargers, setMapVisibleChargers] = useState([]);

  // Feature: Route stops (chargers added as waypoints)
  const [routeStops, setRouteStops] = useState([]);

  // Feature: Route options (fastest/shortest, avoid tolls)
  const [routeOptions, setRouteOptions] = useState({ routeType: 'fastest', avoidTolls: false });

  // Feature: Map pins (lifted from RouteMap)
  const [mapPins, setMapPins] = useState([]);

  // Feature: Via-points (click anywhere on map to force route through a point)
  const [mapViaPoints, setMapViaPoints] = useState([]);
  const [viaPointMode, setViaPointMode] = useState(false);

  // Feature: Map click to set origin/destination
  const [mapMode, setMapMode] = useState(null); // 'setOrigin' | 'setDestination' | null

  // Feature: Help/User Manual modal
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Feature: Routes popup (calculated routes)
  const [showRoutesPopup, setShowRoutesPopup] = useState(false);
  const [selectedAltRoute, setSelectedAltRoute] = useState(null);
  const [excludedOperators, setExcludedOperators] = useState(new Set());
  const [excludedConnectors, setExcludedConnectors] = useState(new Set());
  const [excludedPowerRanges, setExcludedPowerRanges] = useState(new Set());

  // Feature: Group panel
  const [showGroupPanel, setShowGroupPanel] = useState(false);
  const [pendingGroupCount, setPendingGroupCount] = useState(0);

  // Feature: Collapsed filters
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);

  // Feature: Power range configuration for legend
  const [powerRangeConfig, setPowerRangeConfig] = useState(() => {
    try {
      const saved = localStorage.getItem('evplanner-power-ranges');
      if (saved) { const parsed = JSON.parse(saved); if (Array.isArray(parsed) && parsed.length === 4) return parsed; }
    } catch {}
    return DEFAULT_POWER_RANGES;
  });
  const [configSaved, setConfigSaved] = useState(false);

  // Feature: Current user identity (persisted in localStorage)
  const [currentUser, setCurrentUser] = useState(() => {
    try { const saved = localStorage.getItem('evplanner-user'); return saved ? JSON.parse(saved) : null; } catch { return null; }
  });
  useEffect(() => {
    if (currentUser) localStorage.setItem('evplanner-user', JSON.stringify(currentUser));
    else localStorage.removeItem('evplanner-user');
  }, [currentUser]);

  // Load pending group invitations count
  useEffect(() => {
    if (currentUser?.id) {
      fetch(`${API_URL}/api/groups/user/${currentUser.id}/pending`)
        .then(r => r.json())
        .then(d => { if (d.success) setPendingGroupCount(d.data?.length || 0); })
        .catch(() => {});
    } else {
      setPendingGroupCount(0);
    }
  }, [currentUser]);

  // Load language from user profile
  useEffect(() => {
    if (currentUser?.language) {
      changeLocale(currentUser.language);
    }
  }, [currentUser, changeLocale]);

  // Load power range config from user profile when user changes
  useEffect(() => {
    if (currentUser?.powerRangeConfig) {
      try {
        const config = typeof currentUser.powerRangeConfig === 'string'
          ? JSON.parse(currentUser.powerRangeConfig)
          : currentUser.powerRangeConfig;
        if (Array.isArray(config) && config.length === 4) {
          setPowerRangeConfig(config);
          localStorage.setItem('evplanner-power-ranges', JSON.stringify(config));
        }
      } catch {}
    }
  }, [currentUser]);

  const updatePowerRange = (idx, newRange) => {
    setPowerRangeConfig(prev => prev.map((r, i) => i === idx ? { ...r, ...newRange } : r));
  };

  const savePowerRangeConfig = async () => {
    localStorage.setItem('evplanner-power-ranges', JSON.stringify(powerRangeConfig));
    if (currentUser?.id) {
      try {
        await fetch(`${API_URL}/api/users/${currentUser.id}/preferences`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ powerRangeConfig: JSON.stringify(powerRangeConfig) }),
        });
      } catch {}
    }
    setConfigSaved(true);
    setTimeout(() => setConfigSaved(false), 2000);
  };

  const resetPowerRangeConfig = () => {
    setPowerRangeConfig([...DEFAULT_POWER_RANGES]);
    localStorage.removeItem('evplanner-power-ranges');
  };

  // Feature: Settings panel open state (triggered from user icon)
  const [showSettings, setShowSettings] = useState(false);

  // Feature: EV Car configuration
  const [evCarConfig, setEvCarConfig] = useState(() => {
    try { const saved = localStorage.getItem('ev-car-config'); return saved ? JSON.parse(saved) : getDefaultEVCarConfig(); } catch { return getDefaultEVCarConfig(); }
  });
  const handleEvCarConfigChange = useCallback((newConfig) => {
    setEvCarConfig(newConfig);
    localStorage.setItem('ev-car-config', JSON.stringify(newConfig));
  }, []);

  const handleToggleMunicipality = (municipality) => {
    if (!municipality?.id) return;

    setSelectedMunicipalities((prev) => {
      const alreadySelected = prev.some((m) => m.id === municipality.id);
      if (alreadySelected) {
        return prev.filter((m) => m.id !== municipality.id);
      }
      return [...prev, municipality];
    });
  };
  
  // Estado para filtros de cargadores EV
  const [evFilters, setEVFilters] = useState({
    currentTypes: [],
    connectors: [],
    powerRanges: [],
  });
  
  // Estado para el splitter redimensionable
  const [leftWidth, setLeftWidth] = useState(450);
  const [isDragging, setIsDragging] = useState(false);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    e.preventDefault();
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      const container = document.querySelector('.results-layout');
      if (container) {
        const containerRect = container.getBoundingClientRect();
        const newWidth = e.clientX - containerRect.left;
        // Limitar entre 200px y 800px
        setLeftWidth(Math.min(Math.max(newWidth, 200), 800));
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  useEffect(() => {
    let cancelled = false;

    fetch(`${API_URL}/api/health`)
      .then(response => response.json())
      .then(responseData => {
        if (!cancelled) {
          // Handle both Node.js direct response and Java ApiResponse wrapper
          const data = responseData.data || responseData;
          setHealth({ status: "ok", data });
        }
      })
      .catch(err => {
        if (!cancelled) {
          setHealth({ status: "error", error: err.message });
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const onCityChange = (name, value) => {
    setForm(prev => ({ ...prev, [name]: value }));
  };

  // Función reutilizable para calcular ruta dados textos de origen/destino
  const calculateRoute = async (originText, destText, waypoints = null) => {
    setError("");
    setDistancia(null);
    setLoading(true);
    try {
      let originCoords, destCoords;
      originCoords = await geocode(originText);
      destCoords = await geocode(destText);

      const origin = { lat: originCoords.lat, lng: originCoords.lng };
      const destination = { lat: destCoords.lat, lng: destCoords.lng };

      const routeBody = { origin, destination, options: routeOptions };
      if (waypoints && waypoints.length > 0) {
        routeBody.waypoints = waypoints.map(w => ({ lat: w.lat, lng: w.lng }));
      }

      const response = await fetch(`${API_URL}/api/route`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(routeBody),
      });

      const data = await response.json();
      if (!response.ok || data.success === false) {
        throw new Error(data.message || data.error?.message || "Error en la solicitud");
      }

      setDistancia({
        km: data.data.distance,
        duracion: data.data.duration,
        origen: originText,
        destino: destText,
        route: data.data,
        coords: { origin, destination },
      });

      setNearbyData(null);
      setSelectedMunicipalities([]);
      setRouteChargers([]);
      if (!waypoints || waypoints.length === 0) { setRouteStops([]); setMapViaPoints([]); setMapPins([]); }
      setActiveTab('map');
      return data.data;
    } catch (err) {
      const msg = err.message || "";
      if (msg.includes("OSRM") || msg.includes("502") || msg.includes("503") || msg.includes("routing failed")) {
        setError(t("form.routeServiceUnavailable"));
      } else {
        setError(msg || "Error inesperado");
      }
      return null;
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async event => {
    event.preventDefault();

    if (!form.origin.text?.trim() || !form.destination.text?.trim()) {
      setError(t("form.originRequired"));
      return;
    }

    const result = await calculateRoute(form.origin.text, form.destination.text);
    if (result) {
      // Save to route history (including params for reload)
      const historyEntry = {
        origin: form.origin.text,
        destination: form.destination.text,
        km: result.distance,
        date: new Date().toISOString(),
        kmPoints: kmPoints.filter(k => k !== ""),
        maxDeviation,
      };
      setRouteHistory(prev => {
        const updated = [historyEntry, ...prev.filter(h => !(h.origin === historyEntry.origin && h.destination === historyEntry.destination))].slice(0, 20);
        localStorage.setItem('evplanner-history', JSON.stringify(updated));
        return updated;
      });
    }
  };

  // Función para cargar configuración guardada
  const handleLoadConfig = (config) => {
    // Set form values
    setForm({
      origin: { text: config.origin, coordinates: null },
      destination: { text: config.destination, coordinates: null }
    });
    // Set km points and deviation
    setKmPoints(config.kmPoints || []);
    setMaxDeviation(config.maxDeviation || 5);
    // Clear current route data - user needs to recalculate
    setDistancia(null);
    setNearbyData(null);
    setSelectedMunicipalities([]);
    setError("");
  };

  // Función para buscar cargadores en los puntos km de la ruta
  const searchNearbyMunicipalities = async () => {
    if (!distancia || !distancia.route || !distancia.route.geometry) {
      setError("Primero calcula una ruta antes de buscar estaciones");
      return;
    }
    
    // Combine km points from tab + map pins + route stops
    // Excluir puntos demasiado cerca del inicio/fin (no son paradas)
    const margin = Math.min(5, distancia.km * 0.05); // 5km o 5% de la ruta
    const isValidKm = k => !isNaN(k) && k > margin && k < (distancia.km - margin);
    const tabKms = kmPoints.map(k => Number(k)).filter(isValidKm);
    const pinKms = mapPins.map(p => p.km).filter(isValidKm);
    const stopKms = routeStops.map(s => s.km).filter(k => k != null && isValidKm(k));
    
    // Merge all km points, deduplicate (within 2km tolerance)
    const allKms = [...new Set([...tabKms, ...pinKms, ...stopKms])].sort((a, b) => a - b);
    const deduped = [];
    for (const km of allKms) {
      if (!deduped.some(k => Math.abs(k - km) < 2)) deduped.push(km);
    }
    
    if (deduped.length === 0) {
      setError("Introduce al menos un punto km válido (menor que la distancia total)");
      return;
    }
    
    setNearbyLoading(true);
    setError("");
    
    try {
      // Still call the nearby-municipalities endpoint to get search reference points
      const response = await fetch(`${API_URL}/api/route/nearby-municipalities`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          geometry: distancia.route.geometry,
          totalDistance: distancia.km,
          kmPoints: deduped,
          maxDeviation: maxDeviation,
        }),
      });
      
      const data = await response.json();
      if (!response.ok || data.success === false) {
        throw new Error(data.message || data.error?.message || "Error buscando estaciones");
      }
      
      setNearbyData(data.data);
      
      // Search chargers directly at the km point positions on the route
      // Decode route geometry to get lat/lng at each km point
      const routeCoords = distancia.route.geometry.coordinates.map(([lng, lat]) => [lat, lng]);
      const cumDists = [0];
      for (let i = 1; i < routeCoords.length; i++) {
        const [lat1, lng1] = routeCoords[i - 1];
        const [lat2, lng2] = routeCoords[i];
        const R = 6371;
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLng = (lng2 - lng1) * Math.PI / 180;
        const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
        const d = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        cumDists.push(cumDists[i - 1] + d);
      }
      
      const searchPoints = deduped.map(kmVal => {
        for (let i = 0; i < cumDists.length - 1; i++) {
          if (cumDists[i + 1] >= kmVal) {
            const ratio = (kmVal - cumDists[i]) / (cumDists[i + 1] - cumDists[i]);
            const lat = routeCoords[i][0] + ratio * (routeCoords[i + 1][0] - routeCoords[i][0]);
            const lng = routeCoords[i][1] + ratio * (routeCoords[i + 1][1] - routeCoords[i][1]);
            return { lat, lng, km: kmVal };
          }
        }
        return null;
      }).filter(Boolean);
      
      // Fetch chargers at each search point
      const chargerPromises = searchPoints.map(async (pt) => {
        try {
          const params = new URLSearchParams({ lat: pt.lat, lng: pt.lng, radius: String(maxDeviation || 10) });
          if (evFilters?.currentTypes?.length) params.set('currentTypes', evFilters.currentTypes.join(','));
          if (evFilters?.connectors?.length) params.set('connectors', evFilters.connectors.join(','));
          if (evFilters?.powerRanges?.length) params.set('powerRanges', evFilters.powerRanges.join(','));
          const resp = await fetch(`${API_URL}/api/chargers?${params}`);
          const cData = await resp.json();
          return cData.success ? (cData.data || []).map(c => ({ ...c, kmFromOrigin: pt.km })) : [];
        } catch { return []; }
      });
      const results = await Promise.all(chargerPromises);
      // Deduplicate chargers
      const seen = new Set();
      const allChargers = [];
      for (const c of results.flat()) {
        const key = c.id || `${c.coordinates?.lat}-${c.coordinates?.lng}`;
        if (!seen.has(key)) { seen.add(key); allChargers.push(c); }
      }
      setRouteChargers(allChargers);
    } catch (err) {
      setError(err.message || "Error buscando estaciones de carga");
    } finally {
      setNearbyLoading(false);
    }
  };

  // Feature 5: Swap origin/destination
  const handleSwapRoute = () => {
    setForm(prev => ({ origin: prev.destination, destination: prev.origin }));
    setDistancia(null);
    setNearbyData(null);
    setKmPoints([]);
    setSelectedMunicipalities([]);
    setRouteStops([]);
    setMapViaPoints([]);
    setMapPins([]);
  };

  // Clear current route entirely
  const handleClearRoute = () => {
    setForm({ origin: { text: '', coordinates: null }, destination: { text: '', coordinates: null } });
    setDistancia(null);
    setNearbyData(null);
    setKmPoints([]);
    setSelectedMunicipalities([]);
    setRouteChargers([]);
    setRouteStops([]);
    setMapViaPoints([]);
    setMapPins([]);
    setViaPointMode(false);
    setMapMode(null);
    setShowRoutesPopup(false);
    setSelectedAltRoute(null);
  };

  // Feature: Set origin/destination by clicking on map (reverse geocode)
  const reverseGeocode = async (latlng) => {
    try {
      const res = await fetch(`${API_URL}/api/geocode/reverse?lat=${latlng.lat}&lng=${latlng.lng}`);
      const data = await res.json();
      if (data.success && data.data) {
        const d = data.data;
        const addr = d.address || {};
        // Build full address with street and number for maximum precision
        const road = addr.road || addr.pedestrian || addr.footway || '';
        const houseNumber = addr.house_number || '';
        const city = addr.city || addr.town || addr.village || addr.municipality || '';
        const parts = [];
        if (road) parts.push(houseNumber ? `${road} ${houseNumber}` : road);
        if (city && city !== road) parts.push(city);
        if (parts.length > 0) return parts.join(', ');
        return d.display_name?.split(',').slice(0, 3).join(',').trim() || `${latlng.lat.toFixed(5)}, ${latlng.lng.toFixed(5)}`;
      }
    } catch { /* fallback */ }
    return `${latlng.lat.toFixed(5)}, ${latlng.lng.toFixed(5)}`;
  };

  const handleMapSetOrigin = useCallback(async (latlng) => {
    setMapMode(null);
    const name = await reverseGeocode(latlng);
    setForm(prev => ({ ...prev, origin: { text: name, coordinates: latlng } }));
  }, []); // eslint-disable-line

  const handleMapSetDestination = useCallback(async (latlng) => {
    setMapMode(null);
    const name = await reverseGeocode(latlng);
    setForm(prev => ({ ...prev, destination: { text: name, coordinates: latlng } }));
  }, []); // eslint-disable-line

  // Feature: Drag origin marker to new position and recalculate
  const handleDragOrigin = useCallback(async (latlng) => {
    const name = await reverseGeocode(latlng);
    setForm(prev => ({ ...prev, origin: { text: name, coordinates: latlng } }));
    if (distancia) {
      await calculateRoute(name, distancia.destino);
    }
  }, [distancia]); // eslint-disable-line

  // Feature: Drag destination marker to new position and recalculate
  const handleDragDestination = useCallback(async (latlng) => {
    const name = await reverseGeocode(latlng);
    setForm(prev => ({ ...prev, destination: { text: name, coordinates: latlng } }));
    if (distancia) {
      await calculateRoute(distancia.origen, name);
    }
  }, [distancia]); // eslint-disable-line

  // Feature: Suggest charging stops based on EV car config
  const handleSuggestStops = useCallback(async () => {
    if (!distancia || !distancia.route) return;
    const stopKms = suggestChargingStops(distancia.km, evCarConfig);
    if (stopKms.length === 0) return;
    setKmPoints(stopKms.map(String));
  }, [distancia, evCarConfig]);

  // Feature: Refresh route with current options
  const handleRefreshRoute = useCallback(async () => {
    if (!distancia) return;
    const waypoints = [...mapViaPoints, ...routeStops.map(s => ({ lat: s.lat, lng: s.lng }))];
    await calculateRoute(distancia.origen, distancia.destino, waypoints.length > 0 ? waypoints : null);
  }, [distancia, mapViaPoints, routeStops]); // eslint-disable-line

  // Feature: Add map via-point anywhere on map, recalculate route through it
  const handleAddMapViaPoint = useCallback(async (latlng) => {
    const newVia = [...mapViaPoints, latlng];
    setMapViaPoints(newVia);
    setViaPointMode(false);
    if (distancia) {
      const waypoints = [...newVia, ...routeStops.map(s => ({ lat: s.lat, lng: s.lng }))];
      await calculateRoute(distancia.origen, distancia.destino, waypoints);
    }
  }, [mapViaPoints, distancia, routeStops]); // eslint-disable-line

  // Feature: Remove a via-point and recalculate
  const handleRemoveViaPoint = useCallback(async (idx) => {
    const newVia = mapViaPoints.filter((_, i) => i !== idx);
    setMapViaPoints(newVia);
    if (distancia) {
      const waypoints = [...newVia, ...routeStops.map(s => ({ lat: s.lat, lng: s.lng }))];
      await calculateRoute(distancia.origen, distancia.destino, waypoints.length > 0 ? waypoints : null);
    }
  }, [mapViaPoints, distancia, routeStops]); // eslint-disable-line

  // Feature: Add charger as route stop and recalculate
  const handleAddRouteStop = useCallback(async (stop) => {
    const newStops = [...routeStops, stop].sort((a, b) => (a.km || 0) - (b.km || 0));
    setRouteStops(newStops);
    if (distancia) {
      const waypoints = [...mapViaPoints, ...newStops.map(s => ({ lat: s.lat, lng: s.lng }))];
      await calculateRoute(distancia.origen, distancia.destino, waypoints);
    }
  }, [routeStops, distancia, mapViaPoints]); // eslint-disable-line

  // Feature 4: Auto-suggest km points based on vehicle range
  const autoSuggestKmPoints = () => {
    if (!distancia) return;
    const totalKm = distancia.km;
    const range = vehicleRange * 0.8; // safety margin 80%
    const points = [];
    let current = range;
    while (current < totalKm) {
      points.push(String(Math.round(current)));
      current += range;
    }
    if (points.length === 0) points.push(String(Math.round(totalKm / 2)));
    setKmPoints(points);
  };

  // Auto-search nearby chargers when route exists and km sources change
  useEffect(() => {
    if (!distancia || !distancia.route) return;
    const tabKms = kmPoints.map(k => Number(k)).filter(k => !isNaN(k) && k > 0 && k < distancia.km);
    const pinKms = mapPins.map(p => p.km).filter(k => k > 0);
    const stopKms = routeStops.map(s => s.km).filter(k => k != null && k > 0);
    if (tabKms.length === 0 && pinKms.length === 0 && stopKms.length === 0) {
      setRouteChargers([]);
      return;
    }
    const timer = setTimeout(() => {
      searchNearbyMunicipalities();
    }, 600);
    return () => clearTimeout(timer);
  }, [distancia, kmPoints, mapPins, routeStops, maxDeviation, evFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch chargers when municipalities are selected (show on map)
  useEffect(() => {
    if (selectedMunicipalities.length === 0) return;
    let cancelled = false;
    const fetchSelectedChargers = async () => {
      const allNew = [];
      const seen = new Set();
      for (const m of selectedMunicipalities) {
        try {
          const params = new URLSearchParams({ lat: m.lat, lng: m.lng, radius: String(maxDeviation || 10) });
          if (evFilters?.currentTypes?.length) params.set('currentTypes', evFilters.currentTypes.join(','));
          if (evFilters?.connectors?.length) params.set('connectors', evFilters.connectors.join(','));
          if (evFilters?.powerRanges?.length) params.set('powerRanges', evFilters.powerRanges.join(','));
          const resp = await fetch(`${API_URL}/api/chargers?${params}`);
          const data = await resp.json();
          if (data.success && data.data) {
            for (const c of data.data) {
              const key = c.id || `${c.coordinates?.lat}-${c.coordinates?.lng}`;
              if (!seen.has(key)) { seen.add(key); allNew.push(c); }
            }
          }
        } catch { /* ignore */ }
      }
      if (!cancelled) {
        setRouteChargers(prev => {
          const merged = [...prev];
          const existingKeys = new Set(prev.map(c => c.id || `${c.coordinates?.lat}-${c.coordinates?.lng}`));
          for (const c of allNew) {
            const key = c.id || `${c.coordinates?.lat}-${c.coordinates?.lng}`;
            if (!existingKeys.has(key)) merged.push(c);
          }
          return merged;
        });
      }
    };
    fetchSelectedChargers();
    return () => { cancelled = true; };
  }, [selectedMunicipalities]); // eslint-disable-line react-hooks/exhaustive-deps

  // Feature 1: Export route summary to clipboard
  const handleExportRoute = async () => {
    if (!distancia) return;
    let summary = `${t('export.routeSummary')}\n`;
    summary += `${distancia.origen} → ${distancia.destino}\n`;
    summary += `${typeof distancia.km === 'number' ? distancia.km.toFixed(1) : distancia.km} km`;
    if (distancia.duracion) {
      const h = Math.floor(distancia.duracion / 3600);
      const m = Math.floor((distancia.duracion % 3600) / 60);
      summary += ` (${h}h ${m}min)`;
    }
    summary += '\n';
    if (nearbyData?.points) {
      nearbyData.points.forEach(p => {
        summary += `\n--- ${t('nearby.kmFromOrigin', { km: p.kmPoint })} ---\n`;
        [...p.before, ...p.after].forEach(m => {
          summary += `  ${m.name} (${m.provincia}) - ${m.distanceToRoute} km ${t('nearby.columnDeviation').toLowerCase()}\n`;
        });
      });
    }
    try {
      await navigator.clipboard.writeText(summary);
      setExportCopied(true);
      setTimeout(() => setExportCopied(false), 2000);
    } catch { /* fallback */ }
  };

  // Handlers para puntos km
  const addKmPoint = () => {
    setKmPoints([...kmPoints, ""]);
  };

  const removeKmPoint = (index) => {
    const newPoints = kmPoints.filter((_, i) => i !== index);
    setKmPoints(newPoints);
  };

  const updateKmPoint = (index, value) => {
    const newPoints = [...kmPoints];
    newPoints[index] = value;
    setKmPoints(newPoints);
  };

  // Remove a tab km point by value (from map dblclick on tabKmPin)
  const handleRemoveTabKmPoint = useCallback((km) => {
    setKmPoints(prev => prev.filter(k => Math.abs(Number(k) - km) > 2));
  }, []);

  return (
    <div className={`page${darkMode ? ' dark-mode' : ''}`}>
      {/* ===== HEADER ===== */}
      <header className="hero">
        <div className="hero-left">
          <h1>⚡ {t("app.title")}</h1>
          <p>{t("app.subtitle")}</p>
        </div>
        <div className="status">
          <div className="status-top-row">
            <button
              type="button"
              className="user-identity-badge"
              onClick={() => setShowSettings(s => !s)}
              style={{ fontSize: '0.78rem', padding: '2px 8px', borderRadius: '10px', background: currentUser ? '#dbeafe' : '#f3f4f6', color: currentUser ? '#1d4ed8' : '#6b7280', fontWeight: 500, border: '1px solid', borderColor: currentUser ? '#93c5fd' : '#d1d5db', cursor: 'pointer' }}
              title={t("user.login")}
            >
              {currentUser ? `👤 ${currentUser.username}${currentUser.isAdmin ? ' 👑' : ''}` : `👤 ${t("user.anonymous")}`}
            </button>
            {currentUser && (
              <button
                type="button"
                onClick={() => setShowGroupPanel(true)}
                style={{ fontSize: '0.78rem', padding: '2px 8px', borderRadius: '10px', background: '#ede9fe', color: '#6d28d9', fontWeight: 500, border: '1px solid #c4b5fd', cursor: 'pointer', position: 'relative' }}
                title={t("group.badge")}
              >
                👥 {t("group.badge")}
                {pendingGroupCount > 0 && (
                  <span style={{ position: 'absolute', top: '-4px', right: '-4px', background: '#ef4444', color: '#fff', borderRadius: '50%', width: '16px', height: '16px', fontSize: '0.65rem', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>
                    {pendingGroupCount}
                  </span>
                )}
              </button>
            )}
            <LanguageSwitcher currentUser={currentUser} />
            <button type="button" className="dark-toggle-btn" onClick={() => setDarkMode(d => !d)} title={t("darkMode.toggle")}>
              {darkMode ? '☀️' : '🌙'}
            </button>
            <span
              className={`pill ${health.status}`}
              title={
                health.status === "loading" ? t("app.backendChecking")
                : health.status === "ok" ? t("app.backendActive")
                : t("app.backendDown")
              }
              aria-label={
                health.status === "loading" ? t("app.backendChecking")
                : health.status === "ok" ? t("app.backendActive")
                : t("app.backendDown")
              }
            />
          </div>
          <div className="status-secondary">
            <span style={{ fontSize: "0.72rem", color: "#92400e" }}>{t("app.inDevelopment")}</span>
            <div className="progress-bar-mini"><div className="progress-bar-fill" style={{ width: "25%" }} /></div>
          </div>
        </div>
      </header>

      {/* ===== SETTINGS PANEL (floating, triggered by user icon) ===== */}
      {showSettings && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.3)', zIndex: 9998, display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start', paddingTop: '60px', paddingRight: '16px' }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowSettings(false); }}
        >
          <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', maxWidth: '420px', width: '100%', maxHeight: '80vh', overflow: 'auto', position: 'relative' }}>
            <SettingsPanel
              currentRoute={distancia ? { origin: distancia.origen, destination: distancia.destino, distance: distancia.km, kmPoints, maxDeviation } : null}
              onLoadConfig={handleLoadConfig}
              currentUser={currentUser}
              onUserChange={setCurrentUser}
              onClose={() => setShowSettings(false)}
            />
          </div>
        </div>
      )}

      {/* ===== TOP SECTION: Summary ===== */}
      <section className="card top-section">
        {error && (
          <div className="error-popup-overlay" onClick={() => setError("")}>
            <div className="error-popup" onClick={e => e.stopPropagation()}>
              <button className="error-popup-close" onClick={() => setError("")}>✕</button>
              <div className="error-popup-icon">⚠️</div>
              <div className="error-popup-message">{error}</div>
              <button className="error-popup-btn" onClick={() => setError("")}>{t('form.close') || 'Cerrar'}</button>
            </div>
          </div>
        )}
      </section>

      {/* ===== TABS ===== */}
      <div className="tabs-container">
        <div className="tabs-bar">
          <button className={`tab-btn${activeTab === 'map' ? ' active' : ''}`} onClick={() => setActiveTab('map')}>
            🗺️ {t("tab.map")}
          </button>
          <button className={`tab-btn${activeTab === 'stops' ? ' active' : ''}`} onClick={() => setActiveTab('stops')}>
            ⚡ {t("tab.stops")}
          </button>
          <button className={`tab-btn${activeTab === 'search' ? ' active' : ''}`} onClick={() => setActiveTab('search')}>
            ⚡ {t("tab.chargers")}
          </button>
          <button className={`tab-btn${activeTab === 'favorites' ? ' active' : ''}`} onClick={() => setActiveTab('favorites')}>
            ⭐ {t("tab.favorites")} {favoriteChargers.length > 0 && <span className="tab-badge">{favoriteChargers.length}</span>}
          </button>
          <button className={`tab-btn${activeTab === 'history' ? ' active' : ''}`} onClick={() => setActiveTab('history')}>
            📜 {t("tab.history")}
          </button>
          <button className={`tab-btn${activeTab === 'suggestions' ? ' active' : ''}`} onClick={() => setActiveTab('suggestions')}>
            💡 {t("tab.suggestions")}
          </button>
        </div>

        {/* TAB: Map */}
        <div className={`tab-panel${activeTab === 'map' ? ' active' : ''}`}>
          {/* Route form */}
          <div className="top-section-row" style={{ marginBottom: '10px' }}>
            <form onSubmit={onSubmit} className="route-form-inline">
              <div className="route-inputs">
                <label className="route-field">
                  <span className="field-label">{t("form.originLabel")}</span>
                  <CityAutocomplete name="origin" value={form.origin} onChange={(val) => onCityChange("origin", val)} placeholder={t("form.originPlaceholder")} />
                </label>
                <button type="button" className="swap-arrows-h" onClick={handleSwapRoute} title={t("result.swap")}>⇄</button>
                <label className="route-field">
                  <span className="field-label">{t("form.destinationLabel")}</span>
                  <CityAutocomplete name="destination" value={form.destination} onChange={(val) => onCityChange("destination", val)} placeholder={t("form.destinationPlaceholder")} />
                </label>
                <button type="submit" disabled={loading} className="calculate-btn">
                  {loading ? t("form.calculating") : t("form.calculate")}
                </button>
              </div>
            </form>
          </div>
          {/* Route options */}
          <div className="route-options-bar">
            <span className="route-options-label">{t('route.options')}:</span>
            <label className="route-option-radio">
              <input type="radio" name="routeType" value="fastest"
                checked={routeOptions.routeType === 'fastest'}
                onChange={() => setRouteOptions(o => ({ ...o, routeType: 'fastest' }))} />
              🚀 {t('route.fastest')}
            </label>
            {distancia && (
              <button type="button" onClick={handleRefreshRoute} className="calculate-btn" style={{ padding: '3px 10px', fontSize: '0.78rem', marginLeft: 10 }}>
                🔄 Refrescar ruta
              </button>
            )}
            {distancia && (
              <button type="button" onClick={handleSuggestStops} className="calculate-btn" style={{ padding: '3px 10px', fontSize: '0.78rem', marginLeft: 6, background: '#059669' }}>
                ⚡ Sugerir paradas
              </button>
            )}
          </div>
          {/* Route result summary (below options) */}
          {distancia && (
            <div className="route-summary-bar">
              <div className="route-summary-info">
                <strong>{distancia.origen}</strong>
                <span className="route-arrow">→</span>
                <strong>{distancia.destino}</strong>
                <span className="route-distance">{typeof distancia.km === 'number' ? distancia.km.toFixed(1) : distancia.km} {t("params.km")}</span>
                {distancia.duracion && (
                  <span className="route-duration">{t("result.duration", { hours: Math.floor(distancia.duracion / 3600), minutes: Math.floor((distancia.duracion % 3600) / 60) })}</span>
                )}
              </div>
              <button type="button" className="export-btn" onClick={handleExportRoute} title={t("export.copyRoute")}>
                {exportCopied ? `✅ ${t("export.copied")}` : `📋`}
              </button>
            </div>
          )}
          <div className="map-toolbar-elegant">
            <EVCarConfig config={evCarConfig} onChange={handleEvCarConfigChange} />
            <button
              onClick={() => { setShowRoutesPopup(v => !v); setSelectedAltRoute(null); }}
              title="Rutas calculadas"
              style={{
                background: showRoutesPopup ? '#2563eb' : '#fff',
                border: '2px solid #2563eb',
                borderRadius: '50%',
                width: 38,
                height: 38,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '1.2rem',
                boxShadow: '0 2px 8px rgba(37,99,235,0.18)',
                color: showRoutesPopup ? '#fff' : '#2563eb',
              }}
            >
              🛣️
            </button>
            <span
              className={`map-toolbar-item${mapMode === 'setOrigin' ? ' active origin' : ''}`}
              onClick={() => setMapMode(m => m === 'setOrigin' ? null : 'setOrigin')}
              title={t('map.clickSetOrigin')}
            >
              📌 {t('map.markOrigin')}
            </span>
            <span
              className={`map-toolbar-item${mapMode === 'setDestination' ? ' active destination' : ''}`}
              onClick={() => setMapMode(m => m === 'setDestination' ? null : 'setDestination')}
              title={t('map.clickSetDestination')}
            >
              📌 {t('map.markDestination')}
            </span>
            {distancia && (
              <>
                <span
                  className={`map-toolbar-item${viaPointMode ? ' active via' : ''}`}
                  onClick={() => setViaPointMode(v => !v)}
                >
                  📌 {viaPointMode ? t('map.cancelViaPoint') : t('map.markViaPoint')}
                </span>
                {mapViaPoints.length > 0 && (
                  <span className="via-count-badge">🛤️ {mapViaPoints.length}</span>
                )}
                <span
                  className="map-toolbar-item clear-route"
                  onClick={handleClearRoute}
                  title={t('map.clearRoute')}
                >
                  🗑️ {t('map.clearRoute')}
                </span>
              </>
            )}
          </div>
          <RouteMap
            route={distancia?.route}
            origin={distancia ? { ...distancia.coords.origin, name: distancia.origen } : null}
            destination={distancia ? { ...distancia.coords.destination, name: distancia.destino } : null}
            previewOrigin={!distancia && form.origin.coordinates ? { ...form.origin.coordinates, name: form.origin.text } : null}
            previewDestination={!distancia && form.destination.coordinates ? { ...form.destination.coordinates, name: form.destination.text } : null}
            routeChargers={routeChargers}
            totalDistanceKm={distancia?.km}
            filters={evFilters}
            onAddRouteStop={handleAddRouteStop}
            routeStops={routeStops}
            maxDeviation={maxDeviation}
            favoriteChargers={favoriteChargers}
            onToggleFavorite={toggleFavorite}
            viaPointMode={viaPointMode}
            onAddMapViaPoint={handleAddMapViaPoint}
            mapViaPoints={mapViaPoints}
            onRemoveMapViaPoint={handleRemoveViaPoint}
            onPinsChange={setMapPins}
            mapMode={mapMode}
            onSetOrigin={handleMapSetOrigin}
            onSetDestination={handleMapSetDestination}
            kmPointsFromTab={kmPoints}
            onRemoveTabKmPoint={handleRemoveTabKmPoint}
            powerRangeConfig={powerRangeConfig}
            updatePowerRange={updatePowerRange}
            savePowerRangeConfig={savePowerRangeConfig}
            resetPowerRangeConfig={resetPowerRangeConfig}
            configSaved={configSaved}
            currentUser={currentUser}
            maxDeviationValue={maxDeviation}
            onMaxDeviationChange={setMaxDeviation}
            onShowHelp={() => setShowHelpModal(true)}
            mapLoading={nearbyLoading}
            onRefreshSearch={searchNearbyMunicipalities}
            onDragOrigin={handleDragOrigin}
            onDragDestination={handleDragDestination}
            evCarConfig={evCarConfig}
            onSuggestStops={handleSuggestStops}
            selectedAltRoute={selectedAltRoute}
            onOperatorFiltersChange={setExcludedOperators}
            onConnectorFiltersChange={setExcludedConnectors}
            onLegendFiltersChange={setExcludedPowerRanges}
            onVisibleChargersChange={setMapVisibleChargers}
          />
          <RoutesPopup
            visible={showRoutesPopup}
            onClose={() => { setShowRoutesPopup(false); setSelectedAltRoute(null); }}
            route={distancia?.route}
            totalDistanceKm={distancia?.km}
            evCarConfig={evCarConfig}
            favoriteChargers={favoriteChargers}
            filters={evFilters}
            maxDeviation={maxDeviation}
            onSelectRoute={setSelectedAltRoute}
            excludedOperators={excludedOperators}
            excludedConnectors={excludedConnectors}
            excludedPowerRanges={excludedPowerRanges}
            mapChargers={mapVisibleChargers}
          />
        </div>

        {/* TAB: Charging stops */}
        <div className={`tab-panel${activeTab === 'stops' ? ' active' : ''}`}>
          <div className="stops-config compact">
            <div className="stops-auto">
              <label className="param-label">
                {t("params.vehicleRange")}
                <input type="number" value={vehicleRange} onChange={(e) => setVehicleRange(Number(e.target.value) || 300)} min="50" max="900" className="param-input" />
              </label>
              <button type="button" onClick={autoSuggestKmPoints} disabled={!distancia} className="auto-suggest-btn" title={t("params.suggestTooltip")}>
                🔄 {t("params.autoSuggest")}
              </button>
              <label className="param-label">
                {t("params.maxDeviation")}
                <input type="number" value={maxDeviation} onChange={(e) => setMaxDeviation(Number(e.target.value) || 5)} min="1" max="50" className="param-input" />
                <span>{t("params.km")}</span>
              </label>
            </div>
            <div className="stops-manual">
              <span className="km-points-label">{t("params.kmPointsLabel")}</span>
              <div className="km-points-row">
                {kmPoints.map((km, index) => (
                  <div key={index} className="km-point-item">
                    <input type="number" value={km} onChange={(e) => updateKmPoint(index, e.target.value)} placeholder={t("params.kmPlaceholder")} className="km-input" min="1" />
                    <button type="button" onClick={() => removeKmPoint(index)} className="km-remove-btn" title={t("params.removeTooltip")}>×</button>
                  </div>
                ))}
                <button type="button" onClick={addKmPoint} className="km-add-btn" title={t("params.addKmTooltip")}>+</button>
              </div>
            </div>
            <button type="button" onClick={searchNearbyMunicipalities} disabled={!distancia || nearbyLoading} className="calculate-btn" style={{ marginTop: '8px', padding: '6px 16px', fontSize: '0.82rem', alignSelf: 'flex-start' }} title={t("params.recalculateTooltip")}>
              {nearbyLoading ? '⏳ ...' : `🔍 ${t("params.recalculate")}`}
            </button>
          </div>

          {/* Paradas asignadas (cargadores añadidos como waypoints) */}
          {routeStops.length > 0 && (
            <div className="route-stops-section">
              <h4>🛑 {t('map.routeStops')} ({routeStops.length})</h4>
              <div className="via-points-list">
                {routeStops.map((stop, idx) => (
                  <div key={idx} className="via-point-item route-stop-item">
                    <span>
                      ⚡ <strong>{stop.charger?.operator || t('chargers.unknown')}</strong>
                      {stop.charger?.maxPower ? ` · ${stop.charger.maxPower}kW` : ''}
                      {stop.km != null ? ` · km ${Math.round(stop.km)}` : ''}
                    </span>
                    <button
                      className="km-remove-btn"
                      title={t('map.removeStop')}
                      onClick={() => {
                        const newStops = routeStops.filter((_, i) => i !== idx);
                        setRouteStops(newStops);
                        if (distancia) {
                          const waypoints = [...mapViaPoints, ...newStops.map(s => ({ lat: s.lat, lng: s.lng }))];
                          calculateRoute(distancia.origen, distancia.destino, waypoints.length > 0 ? waypoints : null);
                        }
                      }}
                    >×</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Puntos de paso marcados en el mapa */}
          {(mapViaPoints.length > 0 || mapPins.length > 0) && (
            <div className="map-marked-points">
              {mapViaPoints.length > 0 && (
                <div className="map-via-section">
                  <h4>🛤️ {t('map.viaPoints')} ({mapViaPoints.length})</h4>
                  <div className="via-points-list">
                    {mapViaPoints.map((vp, idx) => (
                      <div key={idx} className="via-point-item">
                        <span>🟡 #{idx + 1} — {vp.lat.toFixed(4)}, {vp.lng.toFixed(4)}</span>
                        <button className="km-remove-btn" onClick={() => handleRemoveViaPoint(idx)} title={t('map.removeViaPoint')}>×</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {mapPins.length > 0 && (
                <div className="map-pins-section">
                  <h4>📍 {t('map.mapPins')} ({mapPins.length})</h4>
                  <div className="via-points-list">
                    {mapPins.map((pin, idx) => (
                      <div key={idx} className="via-point-item">
                        <span>🔵 {pin.km} km — {pin.lat.toFixed(4)}, {pin.lng.toFixed(4)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {nearbyData ? (
            <NearbyMunicipalitiesTables nearbyData={nearbyData} onSelectMunicipality={handleToggleMunicipality} selectedMunicipalities={selectedMunicipalities} filters={evFilters} />
          ) : (
            <div className="empty-tab small">
              <span className="empty-icon">⚡</span>
              <p>{t("nearby.addPrompt")}</p>
            </div>
          )}
        </div>

        {/* TAB: Charger search */}
        <div className={`tab-panel${activeTab === 'search' ? ' active' : ''}`}>
          <div className="filters-row" style={{ marginBottom: 10 }}>
            <button type="button" className="filters-toggle" onClick={() => setFiltersCollapsed(c => !c)}>
              {filtersCollapsed ? '▶' : '▼'} {t("filters.title")}
              {totalChargers != null && <span className="total-chargers-badge">⚡ {totalChargers.toLocaleString()}</span>}
            </button>
            {!filtersCollapsed && (
              <EVFilters filters={evFilters} onFiltersChange={setEVFilters} totalChargers={totalChargers} />
            )}
          </div>
          <ChargersList filters={evFilters} onToggleFavorite={toggleFavorite} favoriteChargers={favoriteChargers} />
        </div>

        {/* TAB: Favorites */}
        <div className={`tab-panel${activeTab === 'favorites' ? ' active' : ''}`}>
          {favoriteChargers.length === 0 ? (
            <div className="empty-tab small">
              <span className="empty-icon">⭐</span>
              <p>{t("tab.favoritesEmpty")}</p>
            </div>
          ) : (
            <div className="favorites-list">
              <h3>⭐ {t("tab.favorites")} ({favoriteChargers.length})</h3>
              <div className="chargers-table-container">
                <table className="chargers-table">
                  <thead>
                    <tr>
                      <th>{t("chargers.operator")}</th>
                      <th>{t("chargers.address")}</th>
                      <th>{t("chargers.connectors")}</th>
                      <th>{t("chargers.maxPower")}</th>
                      <th style={{ width: 36, textAlign: 'center' }}>⭐</th>
                    </tr>
                  </thead>
                  <tbody>
                    {favoriteChargers.map((charger, idx) => (
                      <tr key={charger.id || idx}>
                        <td>{charger.operator || t('chargers.unknown')}</td>
                        <td>{charger.address ? (typeof charger.address === 'string' ? charger.address : charger.address.street || charger.address.city || '-') : '-'}</td>
                        <td>{charger.connectors?.map(c => c.type).join(', ') || '-'}</td>
                        <td>{charger.maxPower ? `${charger.maxPower} kW` : '-'}</td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            className="fav-star-btn active"
                            onClick={() => toggleFavorite(charger)}
                            title={t('chargers.unfavorite')}
                          >
                            ★
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* TAB: History */}
        <div className={`tab-panel${activeTab === 'history' ? ' active' : ''}`}>
          {routeHistory.length === 0 ? (
            <div className="empty-tab small">
              <span className="empty-icon">📜</span>
              <p>{t("tab.historyEmpty")}</p>
            </div>
          ) : (
            <div className="history-list">
              <h3>📜 {t("tab.history")}</h3>
              {routeHistory.map((h, idx) => (
                <div key={idx} className="history-item">
                  <div className="history-info">
                    <span className="history-route">{h.origin} → {h.destination}</span>
                    <span className="history-meta">
                      {h.km} km · {new Date(h.date).toLocaleDateString()}
                      {h.kmPoints?.length > 0 && ` · ${h.kmPoints.length} ${t("tab.stops").toLowerCase()}`}
                    </span>
                  </div>
                  <div className="history-actions">
                    <button
                      className="history-load-btn"
                      onClick={async (e) => {
                        e.stopPropagation();
                        setForm({ origin: { text: h.origin, coordinates: null }, destination: { text: h.destination, coordinates: null } });
                        setKmPoints(h.kmPoints || []);
                        setMaxDeviation(h.maxDeviation || 5);
                        setNearbyData(null);
                        setSelectedMunicipalities([]);
                        setRouteChargers([]);
                        setError("");
                        await calculateRoute(h.origin, h.destination);
                      }}
                      title={t("tab.loadRoute")}
                      disabled={loading}
                    >
                      ↻
                    </button>
                    <button
                      className="history-delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setRouteHistory(prev => {
                          const updated = prev.filter((_, i) => i !== idx);
                          localStorage.setItem('evplanner-history', JSON.stringify(updated));
                          return updated;
                        });
                      }}
                      title={t("tab.deleteRoute")}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
              <button className="clear-history-btn" onClick={() => { setRouteHistory([]); localStorage.removeItem('evplanner-history'); }}>
                🗑️ {t("tab.clearHistory")}
              </button>
            </div>
          )}
        </div>

        {/* TAB: Suggestions */}
        <div className={`tab-panel${activeTab === 'suggestions' ? ' active' : ''}`}>
          <SuggestionBox isAdmin={currentUser?.isAdmin || false} />
        </div>
      </div>

      {/* Attribution footer */}
      <div className="attribution-footer">
        Map data &copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> contributors (ODbL) |
        Charger data by <a href="https://openchargemap.org" target="_blank" rel="noopener noreferrer">Open Charge Map</a> (CC BY-SA) |
        Routing by <a href="https://project-osrm.org" target="_blank" rel="noopener noreferrer">OSRM</a>
      </div>
      <div className="legal-footer">
        <a href="/aviso-legal" target="_blank" rel="noopener noreferrer">{t('legal.notice')}</a>
        <a href="/privacidad" target="_blank" rel="noopener noreferrer">{t('legal.privacy')}</a>
        <a href="/cookies" target="_blank" rel="noopener noreferrer">{t('legal.cookies')}</a>
        <a href="/condiciones" target="_blank" rel="noopener noreferrer">{t('legal.terms')}</a>
      </div>
      {legalModal && (
        <div className="legal-modal-overlay" onClick={() => setLegalModal(null)}>
          <div className="legal-modal" onClick={e => e.stopPropagation()}>
            <button className="legal-modal-close" onClick={() => setLegalModal(null)}>✕</button>
            <div className="legal-modal-content">
              {legalTexts[legalModal] ? (
                legalTexts[legalModal].split('\n').map((line, idx) => {
                  const trimmed = line.trim();
                  if (!trimmed) return <div key={idx} style={{ height: '8px' }} />;
                  // Title (ALL CAPS line, no colon)
                  if (/^[A-ZÁÉÍÓÚÜÑ\s]+$/.test(trimmed) && trimmed.length > 3 && !trimmed.endsWith(':'))
                    return <h2 key={idx} style={{ color: '#1d4ed8', fontSize: '1.3em', fontWeight: 700, borderBottom: '2px solid #2563eb', paddingBottom: '6px', marginTop: '16px', marginBottom: '10px' }}>{trimmed}</h2>;
                  // Section heading (line ending with ":")
                  if (trimmed.endsWith(':') && trimmed.length < 80)
                    return <h3 key={idx} style={{ color: '#374151', fontSize: '1.05em', fontWeight: 600, marginTop: '14px', marginBottom: '4px' }}>{trimmed}</h3>;
                  // Bullet point
                  if (trimmed.startsWith('- '))
                    return <div key={idx} style={{ paddingLeft: '20px', position: 'relative', marginBottom: '3px', lineHeight: 1.5 }}><span style={{ position: 'absolute', left: '6px', color: '#2563eb' }}>•</span>{trimmed.substring(2)}</div>;
                  // Normal paragraph
                  return <p key={idx} style={{ margin: '3px 0', lineHeight: 1.6, color: '#374151' }}>{trimmed}</p>;
                })
              ) : (
                <p>Cargando...</p>
              )}
            </div>
          </div>
        </div>
      )}
      {/* User Manual / Help modal */}
      {showHelpModal && (
        <UserManual onClose={() => setShowHelpModal(false)} />
      )}
      {/* Group Panel modal */}
      {showGroupPanel && currentUser && (
        <GroupPanel
          currentUser={currentUser}
          currentRoute={distancia ? { origin: distancia.origen, destination: distancia.destino, distance: distancia.km, kmPoints, maxDeviation } : null}
          onLoadRoute={handleLoadConfig}
          onClose={() => { setShowGroupPanel(false); /* Refresh pending count */
            fetch(`${API_URL}/api/groups/user/${currentUser.id}/pending`).then(r => r.json()).then(d => { if (d.success) setPendingGroupCount(d.data?.length || 0); }).catch(() => {});
          }}
        />
      )}
    </div>
  );
}
